/**
 * Sign-in, over Cognito's SRP flow, on pages we ship ourselves.
 *
 * JP's call (2026-08-10, IDENTITY.md): no hosted UI and no auth domain. The
 * browser never leaves app.easting.ai, so there is no OAuth redirect, no
 * callback URL to keep in sync, and no Cognito-shaped detour on the first
 * screen a paying customer sees. The cost is that login, invite redemption,
 * forgot-password and TOTP enrolment are pages in this app; `LoginPage.tsx`
 * is where they live and this module is the machinery under them.
 *
 * SRP rather than a password grant: the password never crosses the wire even
 * inside TLS, and the app client has `USER_PASSWORD_AUTH` switched off at the
 * pool so nothing can quietly fall back to sending it.
 *
 * Tokens live where the SDK puts them: `localStorage`, keyed by the pool and
 * client ids. That is what lets `restore()` pick a session back up after a
 * refresh instead of charging one sign-in per reload. The XSS exposure that
 * buys is real but not load-bearing: a script running on this origin could
 * call the API as the user with or without readable tokens.
 */

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

/** Injected at build time from the CDK outputs; see `vite.config.ts`. */
export interface PoolConfig {
  userPoolId: string;
  clientId: string;
}

export type AuthState =
  | { kind: "signed-out" }
  | { kind: "new-password"; email: string }
  | { kind: "totp"; email: string }
  | { kind: "signed-in"; email: string };

/** How long before expiry to refresh. A token good for an hour that is
 *  renewed with five minutes left never expires mid-request. */
const REFRESH_MARGIN_MS = 5 * 60 * 1000;

export class Auth {
  private pool: CognitoUserPool | null = null;
  private user: CognitoUser | null = null;
  private session: CognitoUserSession | null = null;
  private challenge: {
    user: CognitoUser;
    attributes: Record<string, unknown>;
  } | null = null;

  constructor(
    config: PoolConfig,
    private readonly onChange: (state: AuthState) => void,
  ) {
    if (config.userPoolId && config.clientId) {
      this.pool = new CognitoUserPool({
        UserPoolId: config.userPoolId,
        ClientId: config.clientId,
      });
    }
  }

  /** False when this build has no pool configured, which is how a local dev
   *  server runs against a local API with a pasted key instead. */
  get configured(): boolean {
    return this.pool !== null;
  }

  get email(): string {
    return this.session?.getIdToken().payload.email ?? "";
  }

  /** True for Easting staff. The group rides the token, so no request is
   *  needed to answer it. */
  get superadmin(): boolean {
    const groups =
      this.session?.getAccessToken().payload["cognito:groups"] ?? [];
    return Array.isArray(groups) && groups.includes("superadmin");
  }

  signIn(email: string, password: string): Promise<AuthState> {
    return new Promise((resolve, reject) => {
      if (!this.pool)
        return reject(new Error("This build has no user pool configured."));
      const user = new CognitoUser({ Username: email, Pool: this.pool });
      user.authenticateUser(
        new AuthenticationDetails({ Username: email, Password: password }),
        {
          onSuccess: (session) => {
            this.adopt(user, session);
            resolve({ kind: "signed-in", email: this.email });
          },
          onFailure: (error) => reject(error),
          // An invited user arrives with a temporary password and has to
          // choose their own before anything else happens. This is invite
          // redemption; there is no separate flow for it.
          newPasswordRequired: (attributes) => {
            this.challenge = { user, attributes: attributes ?? {} };
            const state: AuthState = { kind: "new-password", email };
            this.onChange(state);
            resolve(state);
          },
          totpRequired: () => {
            this.challenge = { user, attributes: {} };
            const state: AuthState = { kind: "totp", email };
            this.onChange(state);
            resolve(state);
          },
        },
      );
    });
  }

  /** Finish invite redemption: the temporary password is spent and this is
   *  the real one. */
  completeNewPassword(password: string): Promise<AuthState> {
    return new Promise((resolve, reject) => {
      const pending = this.challenge;
      if (!pending)
        return reject(new Error("There is no password challenge in progress."));
      // Cognito rejects immutable attributes echoed back at it, and
      // `email_verified` is the one it always sends.
      const attributes = { ...pending.attributes };
      delete (attributes as Record<string, unknown>).email_verified;
      delete (attributes as Record<string, unknown>).email;
      pending.user.completeNewPasswordChallenge(password, attributes, {
        onSuccess: (session) => {
          this.adopt(pending.user, session);
          resolve({ kind: "signed-in", email: this.email });
        },
        onFailure: (error) => reject(error),
      });
    });
  }

  submitTotp(code: string): Promise<AuthState> {
    return new Promise((resolve, reject) => {
      const pending = this.challenge;
      if (!pending)
        return reject(new Error("There is no MFA challenge in progress."));
      pending.user.sendMFACode(
        code,
        {
          onSuccess: (session) => {
            this.adopt(pending.user, session);
            resolve({ kind: "signed-in", email: this.email });
          },
          onFailure: (error) => reject(error),
        },
        "SOFTWARE_TOKEN_MFA",
      );
    });
  }

  forgotPassword(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.pool)
        return reject(new Error("This build has no user pool configured."));
      new CognitoUser({ Username: email, Pool: this.pool }).forgotPassword({
        onSuccess: () => resolve(),
        onFailure: (error) => reject(error),
      });
    });
  }

  confirmForgotPassword(
    email: string,
    code: string,
    password: string,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.pool)
        return reject(new Error("This build has no user pool configured."));
      new CognitoUser({ Username: email, Pool: this.pool }).confirmPassword(
        code,
        password,
        {
          onSuccess: () => resolve(),
          onFailure: (error) => reject(error),
        },
      );
    });
  }

  /**
   * The access token, refreshed silently when it is close to expiring.
   *
   * Every request goes through here, so a session that has been open for an
   * afternoon renews without the user noticing. A refresh token that Cognito
   * has revoked fails here and the app returns to the sign-in page rather
   * than looping on 401s.
   */
  async token(): Promise<string | null> {
    if (!this.session) return null;
    const expiresAt = this.session.getAccessToken().getExpiration() * 1000;
    if (Date.now() > expiresAt - REFRESH_MARGIN_MS) {
      try {
        this.session = await this.refresh();
      } catch {
        this.signOut();
        return null;
      }
    }
    return this.session?.getAccessToken().getJwtToken() ?? null;
  }

  private refresh(): Promise<CognitoUserSession> {
    return new Promise((resolve, reject) => {
      const user = this.user;
      const session = this.session;
      if (!user || !session) return reject(new Error("No session to refresh."));
      user.refreshSession(session.getRefreshToken(), (error, refreshed) => {
        if (error || !refreshed)
          return reject(error ?? new Error("Refresh failed."));
        resolve(refreshed);
      });
    });
  }

  /** Restore a session the SDK already holds, so a reload inside the token's
   *  lifetime does not force a sign-in. */
  restore(): Promise<boolean> {
    return new Promise((resolve) => {
      const user = this.pool?.getCurrentUser();
      if (!user) return resolve(false);
      user.getSession(
        (error: Error | null, session: CognitoUserSession | null) => {
          if (error || !session?.isValid()) return resolve(false);
          this.adopt(user, session);
          resolve(true);
        },
      );
    });
  }

  signOut(): void {
    this.user?.signOut();
    this.user = null;
    this.session = null;
    this.challenge = null;
    this.onChange({ kind: "signed-out" });
  }

  private adopt(user: CognitoUser, session: CognitoUserSession): void {
    this.user = user;
    this.session = session;
    this.challenge = null;
    this.onChange({ kind: "signed-in", email: this.email });
  }
}

/**
 * A stand-in for local development, where there is no pool.
 *
 * `task portal:dev` runs against `task api:up`, which has no Cognito, so the
 * dev flow is the one thing the deployed app refuses: paste an
 * `east_live_` key. That is deliberate and load-bearing in one direction
 * only. The real build never constructs this, because `configured` is true
 * whenever the pool ids were compiled in.
 */
export class PastedKeyAuth {
  private key = "";

  constructor(private readonly onChange: (state: AuthState) => void) {}

  get configured(): boolean {
    return false;
  }

  get email(): string {
    return this.key ? "local development" : "";
  }

  get superadmin(): boolean {
    return false;
  }

  async signInWithKey(key: string): Promise<void> {
    this.key = key.trim();
    this.onChange({ kind: "signed-in", email: this.email });
  }

  async token(): Promise<string | null> {
    return this.key || null;
  }

  signOut(): void {
    this.key = "";
    this.onChange({ kind: "signed-out" });
  }
}
